import { useMemo } from "react";
import type { JSONContent } from "@tiptap/core";
import {
  HistoryIcon,
  PanelRightCloseIcon,
  PencilIcon,
  PlusIcon,
} from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import type { AnimatedTabItem } from "@/components/atoms/AnimatedTabs";
import {
  StripTabBar,
  StripTabCloseButton,
} from "@/components/molecules/StripTabBar";
import { Queries } from "@/constants";
import { useHoverPrefetch } from "@/hooks/useHoverPrefetch";
import { State } from "@/state";
import { NEW_THREAD_THREAD_ID, ThreadIdType } from "@/state/@types";
import { defaultTabLabel, threadNodeIdToTabId } from "@/state/threadTabs";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useThreadTabStatus } from "./Thread.hooks";
import { THREAD_LIST_FILTERS, THREAD_LIST_PAGE_SIZE } from "./Thread.content";

function ThreadTabStartDecorator({
  tabId,
  isDraft,
}: {
  tabId: ThreadIdType;
  /** Words typed in its chat and not sent yet. */
  isDraft: boolean;
}) {
  const { isStreaming, pendingDeltaCount } = useThreadTabStatus(tabId);

  if (!isStreaming && pendingDeltaCount <= 0 && !isDraft) return null;

  return (
    <div className="flex items-center gap-0.75 shrink-0">
      {isStreaming && <Spinner aria-hidden />}
      {pendingDeltaCount > 0 && (
        <HotkeyDecorator hotkey={String(pendingDeltaCount)} />
      )}
      {isDraft && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              data-testid="thread-tab-draft"
              className="flex text-muted-foreground/80"
            >
              <Icon ReactIcon={PencilIcon} size="xs" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">Not sent yet</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

export function ThreadTabBar({
  isListOpen,
  onListToggle,
  onClose,
}: {
  /** Whether the thread list beside the thread is showing. */
  isListOpen: boolean;
  onListToggle: () => void;
  /** Closes the panel hosting the tabs; the button shows only when given. */
  onClose?: () => void;
}) {
  const openThreadTabs = State((s) => s.openThreadTabs);
  const threadTabActivity = State((s) => s.threadTabActivity);
  const threadDrafts = State((s) => s.threadDrafts);
  const activeThreadNodeId = State((s) => s.activeThreadNodeId);
  const selectThreadTab = State((s) => s.selectThreadTab);
  const closeThreadTab = State((s) => s.closeThreadTab);
  const openNewThreadTab = State((s) => s.openNewThreadTab);
  const reorderOpenThreadTabs = State((s) => s.reorderOpenThreadTabs);
  const setThreadTabLabel = State((s) => s.setThreadTabLabel);
  const pinThreadTab = State((s) => s.pinThreadTab);

  // Warm the list's opening page while the pointer rests on the toggle, as the
  // sidebar does for views. Nothing to preload while the list is showing.
  const listPrefetch = useHoverPrefetch(() =>
    isListOpen
      ? null
      : {
          query: Queries.THREADS_CONNECTION_QUERY,
          variables: {
            filter: THREAD_LIST_FILTERS.all,
            first: THREAD_LIST_PAGE_SIZE,
          },
        },
  );

  const activeTabId = threadNodeIdToTabId(activeThreadNodeId);
  const canCloseTab = openThreadTabs.length > 1;

  const tabs: AnimatedTabItem[] = useMemo(
    () =>
      openThreadTabs.map((tab) => {
        // What is typed in its chat and not sent: its words, and the names
        // of what it refers to, in order. A new thread goes by them until it
        // is sent and named; any tab holding some is marked as a draft.
        const said: string[] = [];
        const read = (node?: JSONContent) => {
          if (!node) return;
          if (node.text) said.push(node.text);
          else if (node.attrs?.label) said.push(String(node.attrs.label));
          node.content?.forEach(read);
        };
        read(threadDrafts[tab.id]);
        const draft = said.join(" ").replace(/\s+/g, " ").trim();
        const named = tab.label?.trim();
        const label =
          tab.id === NEW_THREAD_THREAD_ID &&
          draft &&
          (!named || named === defaultTabLabel(tab.id))
            ? `Draft: ${draft}`
            : named || defaultTabLabel(tab.id);
        const activity = threadTabActivity[tab.id];
        const statusLabel = [
          draft ? "draft" : null,
          activity?.isStreaming ? "streaming" : null,
          (activity?.pendingDeltaCount ?? 0) > 0
            ? `${activity!.pendingDeltaCount} pending`
            : null,
        ]
          .filter(Boolean)
          .join(", ");
        const showClose = canCloseTab || tab.id !== NEW_THREAD_THREAD_ID;

        return {
          id: tab.id,
          label,
          "aria-label": statusLabel ? `${label}, ${statusLabel}` : label,
          onLabelRename: (newLabel) => setThreadTabLabel(tab.id, newLabel),
          onPin: tab.isPreview ? () => pinThreadTab(tab.id) : undefined,
          startDecorator: (
            <ThreadTabStartDecorator tabId={tab.id} isDraft={Boolean(draft)} />
          ),
          endDecorator: showClose ? (
            <StripTabCloseButton
              label={label}
              isSelected={tab.id === activeTabId}
              onClose={() => closeThreadTab(tab.id)}
            />
          ) : undefined,
        };
      }),
    [
      openThreadTabs,
      threadTabActivity,
      threadDrafts,
      canCloseTab,
      activeTabId,
      closeThreadTab,
      setThreadTabLabel,
      pinThreadTab,
    ],
  );

  return (
    <StripTabBar
      tabs={tabs}
      selectedTabId={activeTabId}
      onTabChange={(tabId) => selectThreadTab(tabId as ThreadIdType)}
      reorderable
      onTabsReorder={(orderedTabIds) =>
        reorderOpenThreadTabs(orderedTabIds as ThreadIdType[])
      }
      trailingAction={
        <>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="New thread tab"
            onClick={() => openNewThreadTab()}
            className="shrink-0"
          >
            {/* Drawn at its own weight: the added stroke every icon gets made the
                large plus heavier than the icons beside it. */}
            <Icon ReactIcon={PlusIcon} size="lg" />
          </Button>
          <Button
            size="icon-sm"
            variant={isListOpen ? "secondary" : "ghost"}
            aria-label={isListOpen ? "Hide thread list" : "Show thread list"}
            aria-pressed={isListOpen}
            onClick={onListToggle}
            {...listPrefetch}
            className="shrink-0"
          >
            <Icon ReactIcon={HistoryIcon} size="md" />
          </Button>
          {onClose && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Close thread"
                  onClick={onClose}
                  className="shrink-0"
                >
                  <Icon ReactIcon={PanelRightCloseIcon} size="md" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Close thread</TooltipContent>
            </Tooltip>
          )}
        </>
      }
    />
  );
}
